import { Box, Grid, Typography } from '@mui/material'
import { fileUrl } from 'api/api'
import ManageWebApi from 'api/application/manageWeb'
import { PriceListProps } from 'api/application/manageWeb-interface'
import Loading from 'component/Loading'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import MainButton from 'new-components/buttons/main-button'
import AlertModal from 'new-components/modal/alert-modal'
import UploadImage from 'new-components/upload/upload-image'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type PriceListWebProps = {
    priceListImg: string
    setPriceListImg: (img: string) => void
}

const PriceList = (props: PriceListWebProps) => {
    const { t } = useTranslation()
    const t_PRICE_LIST = "MANAGE_WEB.PRICE_LIST"
    const maxHeight = 1080
    const maxWidth = 1080
    const [load, setLoad] = useState<boolean>(true)
    const [priceList, setPriceList] = useState<any>({})
    const [priceListId, setPriceListId] = useState<any>({})
    const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)

    const loadData = async () => {
        setLoad(true)
        await ManageWebApi.getPriceList().then((res) => {
            setPriceList(res.data)
            setPriceListId(res.data)
        }).catch(() => {
            return
        }).finally(() => {
            setLoad(false)
        })
    }

    useEffect(() => {
        loadData()
    }, [])

    const handleDel = () => {
        ManageWebApi.delPriceList(priceList.priceListId).then((resp) => {
            if (resp === "pass") {
                setIsOpenAlertModal(false)
                notiSuccess(t(`${t_PRICE_LIST}.DEL_SUC`))
                setPriceList({})
                setPriceListId({})
                loadData()
            } else {
                setIsOpenAlertModal(false)
                notiError(t(`${t_PRICE_LIST}.SAVE_ERROR`))
            }
        })
    }

    const uploadNewsImageSign = (files: any) => {
        if (files[0]) {
            const file = files[0];
            const fileName = file.name.split(".");
            const detailImage = {
                base64: "",
                type: priceListId?.fileType || "",
                fileExtension: fileName[fileName.length - 1],
                fileSize: file.size.toString(),
                fileId: priceListId?.priceListId || 0,
            };

            const reader: any = new FileReader();
            reader.addEventListener("load", () => {
                const image = new Image();
                image.src = reader?.result as string;
                image.addEventListener("load", () => {
                    const width = image.width;
                    const height = image.height;

                    if (maxHeight < height || maxWidth < width) {
                        const errorWidth = maxWidth < width && ` ${t('MANAGE_WEB.PRICE_LIST.ERROR_WIDTH')} ${maxWidth} ` || ''
                        const errorHeight = maxHeight < height && ` ${t('MANAGE_WEB.PRICE_LIST.ERROR_HEIGHT')} ${maxHeight} ` || ''
                        const error = `${t('MANAGE_WEB.PRICE_LIST.IMAGE')} ${errorWidth} ${maxHeight < height && maxWidth < width && ` ${t('MANAGE_WEB.CONNECTOR.AND')} ` || ''} ${errorHeight}`
                        notiError(error)
                    } else {
                        const base64 = reader.result.split(",");
                        detailImage.base64 = base64.length > 0 ? base64[base64.length - 1] : reader.result;
                        const newData = { ...priceList, ...detailImage, base64Show: reader.result, }
                        setPriceList(newData)
                    }
                });
            });
            reader.readAsDataURL(file);
        }
    };

    const onSavePriceList = async () => {
        const newPayload: PriceListProps = {
            priceListId: priceList?.fileId,
            base64: priceList?.base64 || '',
            type: priceList?.type || '',
            fileExtension: priceList?.fileExtension || '',
            fileSize: priceList?.fileSize || ''
        }

        if (newPayload.base64) {
            await ManageWebApi.savePriceList(newPayload).catch(() => {
                return
            }).then(() => {
                notiSuccess(t(`${t_PRICE_LIST}.SUCCESS_IMG`))
                props.setPriceListImg(priceList?.base64Show || '')
                loadData()
            })
        } else {
            return notiError(t(`${t_PRICE_LIST}.NOTI_IMAGE`))
        }
    }

    const onPriceListRefresh = () => {
        loadData()
    }

    const onRenderImg = () => {
        if (props?.priceListImg && props?.priceListImg !== '') {
            return props?.priceListImg
        } else {
            return `${fileUrl}/${priceList?.filePath}/${priceList?.fileName}`
        }
    }

    return (
        <Box>
            <Loading show={load} />
            <AlertModal
                isOpen={isOpenAlertModal}
                title={t(`MANAGE_WEB.TITLE_DEL`)}
                message={t('MANAGE_WEB.PRICE_LIST.NOTI_DEL')}
                type="error"
                handleClose={() => setIsOpenAlertModal(false)}
                onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
                onSubmit={{ onClick: handleDel, title: t('BUTTON.SUBMIT') }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {!load && (
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={priceList?.filePath ? 6 : 12} md={4} xl={6}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: { xs: 18, sm: 20 },
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    textAlign: { xs: 'left', sm: 'left' },
                                }}
                            >
                                {t(`${t_PRICE_LIST}.TITLE`)}
                            </Typography>
                        </Grid>
                        <Grid item xs={priceList?.filePath ? 6 : 12} md={8} xl={6} display='flex' justifyContent={'flex-end'}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: priceList?.filePath ? 'flex-start' : 'flex-end',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {priceList?.filePath ? (
                                    <MainButton
                                        type="danger"
                                        variant="contained"
                                        title={t(`${t_PRICE_LIST}.BTN_DEL`)}
                                        onClick={() => { setIsOpenAlertModal(true) }}
                                        style={{ width: 120 }}
                                    />
                                ) : (
                                    <>
                                        <MainButton
                                            type="secondary"
                                            variant={'outlined'}
                                            title={t(`${t_PRICE_LIST}.BTN_CANCEL`)}
                                            onClick={() => { onPriceListRefresh() }}
                                            style={{ width: 120 }}
                                            disabled={!priceList?.base64Show}
                                        />

                                        <MainButton
                                            type="primary"
                                            variant="contained"
                                            title={t(`${t_PRICE_LIST}.BTN_SAVE`)}
                                            onClick={() => { onSavePriceList() }}
                                            style={{ width: 120 }}
                                            disabled={!priceList?.base64Show}
                                        />
                                    </>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                )}

                {!load &&
                    <Box sx={{ marginTop: '16px', width: '100%', height: 'calc(100vh - 30vh)', overflow: 'auto' }}>
                        {priceList?.filePath &&
                            <img
                                src={onRenderImg()}
                                style={{ width: '100%', height: 'auto', display: 'block' }}
                            />
                            ||
                            <Box>
                                <UploadImage
                                    setValue={uploadNewsImageSign}
                                    pathFile={priceList?.base64Show || priceList?.filePath || ''}
                                    canDelete
                                    onDelete={() => setPriceList({})}
                                    height={'calc(100vh - 380px)'}
                                    maxWidth={maxWidth}
                                    maxHeight={maxHeight}
                                />
                                <Box color={"#BBBBBB"} marginTop='8px' textAlign={'left'} width={'100%'}>
                                    {'('}{t(`MANAGE_WEB.BANNER.FILESIZE`)}{` ${maxWidth} x ${maxHeight} px)`}
                                </Box>
                            </Box>
                        }
                    </Box>
                }
            </Box>
        </Box >
    )
}

export default PriceList